import { useState, useRef, useEffect } from 'react';

import { mergeChunks, float32To16BitPCMBuffer } from '../../../service/AudioService';
import { useEventCallback } from '../../useEventCallback/useEventCallback';
import { connectToIntentAudioSocket } from '../../../api/authApis';
import type { TestTaker } from '../../../interface/interface';
import type { Prompt } from '../../../interface/Prompt';
import type { IntentData } from '../../../interface/IntentData';
import { useControlledInterval } from '../../useControlledInterval/useControlledInterval';

interface Options {
    testTaker: TestTaker;
    currentPrompt: Prompt;
    recordedChunks: Float32Array[];
    onIntentReceived: (intent: IntentData) => void;
    testEnded: boolean;
}

// const CHUNKS_START_OFFSET = 50; NOSONAR
const CHUNKS_START_OFFSET = 5;
const CHUNKS_LAST_OFFSET = 0;
const INTENT_MAX_DURATION = 4;

const PING_INTERVAL = 150; // seconds

export const useIntentDetection = (props: Options) => {
    const { recordedChunks, onIntentReceived, testTaker, currentPrompt, testEnded } = props;

    const [isWSClosed, setIsWSClosed] = useState(true);
    const [wsReady, setWSReady] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const intentDetectionActiveRef = useRef(false);
    const intentStartIndexRef = useRef(0);
    const intentUsedRef = useRef(false);
    const intentTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
    const webSocketRef = useRef<WebSocket>();
    const dummyCalledRef = useRef(false);
    const initialSpeechEndedRef = useRef(false);
    const pingInterval = useControlledInterval();
    const lastIntentPromptRef = useRef<Prompt>();
    const currentPromptRef = useRef<Prompt>();

    const _onIntentReceived = useEventCallback(onIntentReceived);

    const startIntentRecording = () => {
        // check ref instaed of state due to setTimeout issues
        if (intentStartIndexRef.current) return;

        intentStartIndexRef.current = Math.max(0, recordedChunks.length - CHUNKS_START_OFFSET);
        intentDetectionActiveRef.current = true;

        intentTimeoutRef.current = setTimeout(() => {
            stopIntentRecording();
        }, INTENT_MAX_DURATION * 1000);
    };

    const stopIntentRecording = () => {
        // check ref instaed of state due to settimeout issues
        if (!intentStartIndexRef.current) return;

        clearTimeout(intentTimeoutRef.current);

        if (recordedChunks.length) {
            const partialChunks = recordedChunks.slice(
                intentStartIndexRef.current,
                recordedChunks.length - CHUNKS_LAST_OFFSET,
            );

            const merged = mergeChunks(partialChunks);
            const l16Buffer = float32To16BitPCMBuffer(merged);

            sendIntent(l16Buffer);
        }

        intentStartIndexRef.current = 0; // Reset start index
    };

    const createWebSocket = () => {
        webSocketRef.current = connectToIntentAudioSocket(testTaker);
        webSocketRef.current?.addEventListener('message', (event: any) => {
            console.log('Message received from audio intent', event.data);
            try {
                const data: IntentData = JSON.parse(JSON.parse(event?.data));
                const testMovedOn = lastIntentPromptRef.current !== currentPromptRef.current;

                if (testMovedOn) console.log('Ignoring intent result: test has moved on to next prompt');

                if (dummyCalledRef.current && intentDetectionActiveRef.current && !testMovedOn) {
                    console.log('Intent data received:', data);
                    _onIntentReceived?.(data);
                }

                dummyCalledRef.current = true;
                intentDetectionActiveRef.current = false;
            } catch (error) {
                console.log('Error while parsing intent response', error);
            }
        });
        webSocketRef.current?.addEventListener('open', () => {
            console.log('Audio Web socket is opened');
            setWSReady(true);
            setIsWSClosed(false);

            pingInterval.start(() => webSocketRef.current?.send('ping'), PING_INTERVAL * 1000);

            if (dummyCalledRef.current) return;

            // dummy audio call: fetch an MP3, decode it, convert to 16-bit PCM, send to websocket
            (async () => {
                try {
                    const resp = await fetch('/assets/audios/dummy-intent-audio.mp3');
                    const mp3ArrayBuffer = await resp.arrayBuffer();

                    // decode MP3 to Float32 via WebAudio
                    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
                    const audioCtx = new AudioCtx();
                    const decoded = await audioCtx.decodeAudioData(mp3ArrayBuffer);

                    const channelCount = decoded.numberOfChannels;
                    const len = decoded.length;
                    let mono: Float32Array;
                    if (channelCount === 1) {
                        mono = decoded.getChannelData(0);
                    } else {
                        mono = new Float32Array(len);
                        for (let i = 0; i < len; i++) {
                            let sum = 0;
                            for (let c = 0; c < channelCount; c++) {
                                sum += decoded.getChannelData(c)[i];
                            }
                            mono[i] = sum / channelCount;
                        }
                    }

                    const pcm16Buffer = float32To16BitPCMBuffer(mono);

                    if (webSocketRef.current?.readyState === WebSocket.OPEN) {
                        webSocketRef.current.send(pcm16Buffer);
                        dummyCalledRef.current = true; 
                    }

                    audioCtx.close();
                } catch (err) {
                    console.error('Failed to send dummy audio', err);
                    setError('V111'); 
                }
            })();
        });
        webSocketRef.current?.addEventListener('error', () => {
            console.error('Audio Web socket is having error');
            setError('V110');
        });
        webSocketRef.current?.addEventListener('close', () => {
            console.log('Audio Web socket is closed');
            pingInterval.clear();
            setIsWSClosed(true);
        });
    };

    const sendIntent = (data: ArrayBuffer) => {
        if (!intentDetectionActiveRef.current) return;

        if (webSocketRef.current) {
            console.log('Sending intent audio:', data);
            lastIntentPromptRef.current = currentPromptRef.current;

            webSocketRef.current.send(data);

            intentUsedRef.current = true;
        } else {
            console.error('Error sending intent audio. WebSocket is not connected');
        }
    };

    const resetIntentDetection = () => {
        clearTimeout(intentTimeoutRef.current);
        intentStartIndexRef.current = 0;
        intentUsedRef.current = false;
        intentDetectionActiveRef.current = false;
        initialSpeechEndedRef.current = false;
    };

    const cleanupWebSocket = () => {
        pingInterval.clear();
        webSocketRef.current?.close();
        webSocketRef.current = undefined;
    };

    useEffect(() => {
        return () => cleanupWebSocket();
    }, []);

    useEffect(() => {
        if (isWSClosed && !testEnded) {
            cleanupWebSocket();
            createWebSocket();
        }
    }, [isWSClosed, testEnded]);

    useEffect(() => {
        if (!currentPrompt) return;

        currentPromptRef.current = currentPrompt;

        return () => resetIntentDetection();
    }, [currentPrompt]);

    return {
        wsReady,
        error,
        startIntentRecording,
        resetIntentDetection,
        intentUsedRef,
        intentDetectionActiveRef,
        initialSpeechEndedRef,
    };
};
